// Month labels starting from April 2026
const MONTH_NAMES = [
  'Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc',
];

export const START_MONTH = 3; // April = index 3 (0-based)
export const START_YEAR = 2026;

export function moisLabel(mi: number): string {
  const totalMonth = (START_MONTH + mi) % 12;
  const year = START_YEAR + Math.floor((START_MONTH + mi) / 12);
  return `${MONTH_NAMES[totalMonth]} ${year}`;
}

export function moisShort(mi: number): string {
  const totalMonth = (START_MONTH + mi) % 12;
  const year = START_YEAR + Math.floor((START_MONTH + mi) / 12);
  return `${MONTH_NAMES[totalMonth]} ${String(year).slice(2)}`;
}

export function exerciceLabel(ex: number, ranges?: [number, number][]): string {
  const r = ranges ?? [[0, 11], [12, 23], [24, 35]];
  if (ex >= r.length) return `Exercice ${ex + 1}`;
  const [startMi, endMi] = r[ex];
  const startYear = START_YEAR + Math.floor((START_MONTH + startMi) / 12);
  const endYear = START_YEAR + Math.floor((START_MONTH + endMi) / 12);
  if (startYear === endYear) return `Exercice ${ex + 1} (${startYear})`;
  return `Exercice ${ex + 1} (${startYear}/${endYear})`;
}

/**
 * Calcule les bornes [start, end] de chaque exercice fiscal.
 * Aucun cap : la durée totale = ex1 + ex2 + ex3.
 */
export function getExRanges(ex1: number = 12, ex2: number = 12, ex3: number = 12): [number, number][] {
  const d1 = Math.max(1, Math.round(ex1));
  const d2 = Math.max(1, Math.round(ex2));
  const d3 = Math.max(1, Math.round(ex3));
  return [
    [0,            d1 - 1],
    [d1,           d1 + d2 - 1],
    [d1 + d2,      d1 + d2 + d3 - 1],
  ];
}

/** Retourne le nombre total de mois sur les 3 exercices. */
export function getTotalMonths(ex1: number = 12, ex2: number = 12, ex3: number = 12): number {
  return Math.max(1, Math.round(ex1)) + Math.max(1, Math.round(ex2)) + Math.max(1, Math.round(ex3));
}

/** Convertit un indice de mois en date de fin de mois (YYYY-MM-DD). Pas de cap. */
export function miToEndDate(mi: number): string {
  const calMonth = (START_MONTH + mi) % 12;
  const calYear = START_YEAR + Math.floor((START_MONTH + mi) / 12);
  const lastDay = new Date(calYear, calMonth + 1, 0).getDate();
  return `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/** Convertit une date (YYYY-MM-DD) en indice de mois. Pas de cap. */
export function endDateToMi(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return 11;
  const endTotal = d.getFullYear() * 12 + d.getMonth();
  const startTotal = START_YEAR * 12 + START_MONTH;
  return Math.max(0, endTotal - startTotal);
}

/** Remplace les espaces insécables / fines (fr-FR) par des espaces normaux pour une lecture claire. */
function normalizeFrSpaces(s: string): string {
  return s.replace(/\u00a0|\u202f/g, ' ');
}

/**
 * Montants en milliers d’euros pour axes / infobulles (ex. « 125 k€ », « 1 250 k€ »).
 */
export function formatKEur(value: number): string {
  const k = Math.round(value / 1000);
  const s = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(k);
  return normalizeFrSpaces(`${s} k€`);
}

export function formatCurrency(value: number, compact = false): string {
  if (compact && Math.abs(value) >= 1000000) {
    const m = value / 1000000;
    const s = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(m);
    return normalizeFrSpaces(`${s} M€`);
  }
  if (compact && Math.abs(value) >= 1000) {
    const k = Math.round(value / 1000);
    const s = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(k);
    return normalizeFrSpaces(`${s} k€`);
  }
  const s = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
  return normalizeFrSpaces(s);
}

export function formatPct(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatNumber(value: number): string {
  return normalizeFrSpaces(new Intl.NumberFormat('fr-FR').format(Math.round(value)));
}

export function parseCurrency(str: string): number {
  const cleaned = str.replace(/[^0-9,.-]/g, '').replace(',', '.');
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

export const MOIS_OPTIONS = Array.from({ length: 36 }, (_, i) => ({
  value: i,
  label: `M${i + 1} — ${moisLabel(i)}`,
}));

/** Convertit une durée depuis le mois 0 en date de fin (raccourci). */
export function dureeToEndDate(duree: number): string {
  return miToEndDate(Math.max(0, duree - 1));
}

/** Convertit une date de fin en durée depuis le mois 0 (raccourci). */
export function endDateToDuree(dateStr: string): number {
  return endDateToMi(dateStr) + 1;
}
